import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import * as applicationStatuses from "./applicationStatus";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import { getProductData } from "../productService";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromIban,
  fromNumber,
  fromOpposingPartyType,
  fromYear,
  getConvertedFormDataBase,
  hasChangedData,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizeOrganizationEmailData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toIban,
  toNumber,
  toOpposingPartyType,
  toYear,
} from "../formServiceFunctions";
import _has from "lodash/has";
import _merge from "lodash/merge";
import ApiClient from "../apiClient";
import { Customer } from "../../types/Entities/Customer";
import _isEmpty from "lodash/isEmpty";
import { Person } from "../../types/Entities/Person";
import _pick from "lodash/pick";
import { isFlexServiceProduct } from "../advocardService";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (product: AbstractCase): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_BASE_DATA]: {
      employmentContract: [
        "caseCategory",
        "affectedPartyType",
        "clientWasMinorAtContractDate",
        "clientLegalRepresentativeAmount",
        "employmentStartDate",
        "employmentEndDate",
        "employmentRegularWorkplace",
        "employmentRegularWorkplaceOtherAddressDescription",
        "subjectOfDispute",
        "clientHasAlreadyClaimed",
        "claimedDateClient",
        "claimedDeadlineClient",
        "claimedAmountClient",
        "defenseProcessStatus",
        "paymentOrderDate",
        "enforcementOrderDate",
        "statementOfClaimFiledDate",
        "overdueFees",
      ],
      employmentRegularWorkplaceOtherAddress: ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      client: ["gender", "givenName", "familyName", "birthDate"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      "client.bankData": ["iban"],
      clientLegalRepresentative: ["gender", "givenName", "familyName"],
      "clientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      secondClientLegalRepresentative: ["gender", "givenName", "familyName"],
      "secondClientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_CASE_DETAILS]: {
      employmentContract: [
        "reasonsForDispute",
        "salaryOutstandingStartDate",
        "salaryOutstandingEndDate",
        "grossSalary",
        "overtimeAccumulatedStartDate",
        "overtimeAccumulatedEndDate",
        "overtimeAccumulatedTotal",
        "weeklyWorkingHours",
        "hasOvertimeSurcharge",
        "overtimeSurchargeDetails",
        "hasOvertimeRecord",
        "yearOfVacationClaim",
        "vacationDaysLeft",
        "grossSalarySumLastThreeMonths",
        "vacationDenialReason",
        "hoursLeisureCompensation",
        "workCertificateRequest",
        "workCertificateIssuanceDenied",
        "workCertificateIssuanceDenialReason",
        "workCertificateReceivedAtDate",
        "workCertificateCorrectionRequest",
        "parentalLeaveStartDate",
        "parentalLeaveEndDate",
        "hasTakenParentalLeaveBefore",
        "parentalLeaveSplit",
        "parentalLeaveRequestOnTime",
        "parentalLeaveRequestDenied",
        "parentalLeaveRequestDeniedReason",
        "partTimeStartDate",
        "partTimeDesiredWeeklyWorkingHours",
        "numberOfEmployees",
        "partTimeDenialReason",
        "incidentType",
        "hasWitness",
        "witnessCount",
        "incidentStartDate",
        "incidentObjectiveClient",
        "claimedPaymentTypes",
        "claimedPaymentReason",
        "allegedDamageTypes",
        "allegedDamageDate",
        "writtenWarningReceivedDate",
        "writtenWarningReason",
        "writtenWarningIllegalReasons",
        "terminationAgreementOfferDate",
        "terminationAgreementDeadlineDate",
        "terminationAgreementIncludesSettlement",
        "offeredSettlementPayment",
        "terminationAgreementObjectivesClient",
        "itemsToReturn",
        "itemsToReturnReason",
        "itemsToReturnRejectionReasons",
      ],
      witnesses: ["gender", "givenName", "familyName", "email", "telephone"],
      "witnesses.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      witnessesToBeRemoved: ["id"],
      "opposingPartyLegalEntity.organization.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]: {
      employmentContract: ["opposingPartyType", "contractualPartnerHasLawyer"],
      "opposingPartyLegalEntity.organization": ["legalName", "telephone"],
      "opposingPartyLegalEntity.organization.mainEmail": ["email"],
      "opposingPartyLegalEntity.organization.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName", "email", "telephone"],
      "opposingPartyPerson.residenceAddress": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      "opponentLawFirm.organization": ["name", "telephone"],
      "opponentLawFirm.organization.mainEmail": ["email"],
      "opponentLawFirm.organization.address": ["streetAddress", "postalCode", "addressLocality"],
      opponentLawyer: ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_DESCRIPTION]: {
      employmentContract: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      employmentContract: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "employmentStartDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "employmentEndDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientWasMinorAtContractDate",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientLegalRepresentativeAmount",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "clientHasAlreadyClaimed",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "claimedDateClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "claimedDeadlineClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "claimedAmountClient",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "client.bankData.iban",
      convertFrom: fromIban,
      convertTo: toIban,
    },
    {
      path: "salaryOutstandingStartDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "salaryOutstandingEndDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "grossSalary",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "overtimeAccumulatedStartDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "overtimeAccumulatedEndDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "overtimeAccumulatedTotal",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "weeklyWorkingHours",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "hasOvertimeSurcharge",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "hasOvertimeRecord",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "yearOfVacationClaim",
      convertFrom: fromYear,
      convertTo: toYear,
    },
    {
      path: "vacationDaysLeft",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "grossSalarySumLastThreeMonths",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "hoursLeisureCompensation",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "workCertificateIssuanceDenied",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "workCertificateReceivedAtDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "parentalLeaveStartDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "parentalLeaveEndDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "hasTakenParentalLeaveBefore",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "parentalLeaveSplit",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "parentalLeaveRequestOnTime",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "parentalLeaveRequestDenied",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "partTimeStartDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "partTimeDesiredWeeklyWorkingHours",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "numberOfEmployees",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "hasWitness",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "incidentStartDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "opposingPartyType",
      convertFrom: fromOpposingPartyType,
      convertTo: toOpposingPartyType,
    },
    {
      path: "contractualPartnerHasLawyer",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "paymentOrderDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "enforcementOrderDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "statementOfClaimFiledDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "overdueFees",
      convertFrom: fromFloat,
      convertTo: (value: string | null) => (value === null ? 0 : toFloat(value)),
    },
    {
      path: "allegedDamageDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "writtenWarningReceivedDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "terminationAgreementOfferDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "terminationAgreementDeadlineDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "terminationAgreementIncludesSettlement",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "offeredSettlementPayment",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
  ];

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    createMutation,
    refreshCustomer,
    deleteMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields(product)[applicationStatus] || {};
    const convertedFormData = getConvertedFormDataBase(formData, this.conversionFields);

    let hasCustomerUpdates = false;
    let clearGenerationFields = false;

    if (_has(fields, "employmentRegularWorkplaceOtherAddress")) {
      let addressData = _pick(convertedFormData.employmentRegularWorkplaceOtherAddress, fields.employmentRegularWorkplaceOtherAddress);
      addressData = sanitizeAddressData(addressData);

      if (hasChangedData(addressData, product.employmentRegularWorkplaceOtherAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.employmentRegularWorkplaceOtherAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "customer.person.residenceAddress") && !!customer.person?.residenceAddress?.id) {
      hasCustomerUpdates = true;
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: customer.person.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      hasCustomerUpdates = true;
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = pickAndConvert(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = ["customer", "unknown"].includes(formData.affectedPartyType)
        ? formData.customer.person.residenceAddress
        : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "witnesses")) {
      // Promise is used because map does not wait for the async calls;
      // this waits until all async(await) operations complete before continuing.
      await Promise.all(
        formData.witnesses.map(async (witness: Omit<Person, "id"> & { id: string | number }) => {
          const witnessData = {
            givenName: witness.givenName,
            familyName: witness.familyName,
            gender: witness.gender,
            type: "processParticipant",
            labelType: "witness",
            email: witness.email,
            telephone: witness.telephone,
            customerSpecifiedCaseId: product.id,
          };

          let addressData = pickAndConvert(witness.residenceAddress, fields[`witnesses.residenceAddress`]);
          addressData = sanitizeAddressData(addressData);

          if (typeof witness.id === "string" && witness.id.startsWith("new-")) {
            clearGenerationFields = true;

            const personEntity: Person = await createMutation.mutateAsync({
              uri: "/people",
              data: witnessData,
            });

            await updateMutation.mutateAsync({
              uri: "/addresses",
              id: personEntity.residenceAddress.id,
              data: addressData,
            });
          } else {
            const originalData = product.witnesses.find((witnessItem: Person) => witnessItem.id === witness.id);

            if (hasChangedData(witnessData, originalData, ["customerSpecifiedCaseId"])) {
              clearGenerationFields = true;

              await updateMutation.mutateAsync({
                uri: "/people",
                id: witness.id as number,
                data: witnessData,
              });
            }

            if (hasChangedData(addressData, originalData.residenceAddress)) {
              await updateMutation.mutateAsync({
                uri: "/addresses",
                id: witness.residenceAddress.id,
                data: addressData,
              });
            }
          }
        })
      );
    }

    // Optional chaining is required because `witnessesToBeRemoved` may be undefined
    // in certain cases where the Witnesses component is not mounted.
    if (_has(fields, "witnessesToBeRemoved") && formData.witnessesToBeRemoved?.length > 0) {
      clearGenerationFields = true;

      // Promise is used because map does not wait for the async calls;
      // this waits until all async(await) operations complete before continuing.
      await Promise.all(
        formData.witnessesToBeRemoved.map(async (id: number) => {
          await deleteMutation.mutateAsync({
            uri: "/people",
            id: id,
          });
        })
      );
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = ["customer", "unknown"].includes(formData.affectedPartyType) ? convertedFormData.customer.person : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "clientLegalRepresentative")) {
      let clientLegalRepresentativeData = pickAndConvert(formData.clientLegalRepresentative, fields.clientLegalRepresentative);
      clientLegalRepresentativeData = sanitizePersonData(clientLegalRepresentativeData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.clientLegalRepresentative.id,
        data: clientLegalRepresentativeData,
      });
    }

    if (_has(fields, "clientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.clientLegalRepresentative.residenceAddress, fields["clientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.clientLegalRepresentative.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "secondClientLegalRepresentative")) {
      let secondClientLegalRepresentativeData = pickAndConvert(formData.secondClientLegalRepresentative, fields.secondClientLegalRepresentative);
      secondClientLegalRepresentativeData = sanitizePersonData(secondClientLegalRepresentativeData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.secondClientLegalRepresentative.id,
        data: secondClientLegalRepresentativeData,
      });
    }

    if (_has(fields, "secondClientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.secondClientLegalRepresentative.residenceAddress, fields["secondClientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.secondClientLegalRepresentative.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "opponentLawFirm.organization")) {
      let opponentLawFirmData = pickAndConvert(formData.opponentLawFirm.organization, fields["opponentLawFirm.organization"]);
      opponentLawFirmData = sanitizeOrganizationData(opponentLawFirmData);
      opponentLawFirmData.name = opponentLawFirmData.name || (formData.opponentLawyer.familyName ? `Kanzlei ${formData.opponentLawyer.familyName}` : "");
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: formData.opponentLawFirm.organization.id,
        data: opponentLawFirmData,
      });
    }

    if (_has(fields, "opponentLawFirm.organization.mainEmail")) {
      let emailData = pickAndConvert(formData.opponentLawFirm.organization.mainEmail, fields["opponentLawFirm.organization.mainEmail"]);
      emailData = sanitizeOrganizationEmailData(emailData);
      await updateMutation.mutateAsync({
        uri: "/organization_emails",
        id: product.opponentLawFirm.organization.mainEmail.id,
        data: emailData,
      });
    }

    if (_has(fields, "client.bankData")) {
      const bankData = pickAndConvert(formData.client.bankData, fields["client.bankData"]);
      if (bankData.iban) {
        bankData.iban = toIban(bankData.iban);
      } else {
        delete bankData.iban;
      }
      if (!_isEmpty(bankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.client.bankData.id,
          data: bankData,
        });
      }
    }

    if (_has(fields, "opponentLawFirm.organization.address")) {
      let opponentLawFirmAddressData = pickAndConvert(formData.opponentLawFirm.organization.address, fields["opponentLawFirm.organization.address"]);
      opponentLawFirmAddressData = sanitizeAddressData(opponentLawFirmAddressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: formData.opponentLawFirm.organization.address.id,
        data: opponentLawFirmAddressData,
      });
    }

    if (_has(fields, "opponentLawyer")) {
      let opponentLawyerData = pickAndConvert(formData.opponentLawyer, fields["opponentLawyer"]);
      opponentLawyerData = sanitizePersonData(opponentLawyerData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: formData.opponentLawyer.id,
        data: opponentLawyerData,
      });
    }

    if (formData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity.organization")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyLegalEntity.organization, fields["opposingPartyLegalEntity.organization"]);
        opposingPartyData = sanitizeOrganizationData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.opposingPartyLegalEntity.organization.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.mainEmail")) {
        let emailData = pickAndConvert(formData.opposingPartyLegalEntity.organization.mainEmail, fields["opposingPartyLegalEntity.organization.mainEmail"]);
        emailData = sanitizeOrganizationEmailData(emailData);
        await updateMutation.mutateAsync({
          uri: "/organization_emails",
          id: product.opposingPartyLegalEntity.organization.mainEmail.id,
          data: emailData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.address")) {
        let addressData = pickAndConvert(formData.opposingPartyLegalEntity.organization.address, fields["opposingPartyLegalEntity.organization.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyLegalEntity.organization.address.id,
          data: addressData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyPerson, fields.opposingPartyPerson);
        opposingPartyData = sanitizePersonData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.opposingPartyPerson.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = pickAndConvert(formData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyPerson.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "caseInsurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let caseInsuranceData = pickAndConvert(convertedFormData.caseInsurance, fields["caseInsurance"]);
      caseInsuranceData.insurer = caseInsuranceData.insurer["@id"];

      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? formData.customer.person : formData.caseInsurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, "employmentContract")) {
      productData = _merge(productData, pickAndConvert(formData, fields.employmentContract), pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }
    await updateMutation.mutateAsync({
      uri: getProductData("employmentContract", "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Partial<AbstractCase>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    currentlyEmployed: "",
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    const formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    if (isFlexServiceProduct(product)) {
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_BASE_DATA]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_DESCRIPTION]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    } else {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_BASE_DATA]);

      if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
        if (formData.subjectOfDispute !== "other") {
          availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CASE_DETAILS]);
        }
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]);
      }

      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DESCRIPTION]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);

      if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);
      }

      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);

      if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
      }

      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    }

    return availableSteps;
  };
}

export default new FormService();
